import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

// Load 65 images automatically
const imageList = Array.from({ length: 65 }, (_, i) => `/new-collection/${i + 1}.png`);

const NewCollection: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const LENS_SIZE = 120;
  const ZOOM_FACTOR = 2;

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || isMobile) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    
    // Get mouse position relative to the image
    const mouseX = e.clientX - imageRect.left;
    const mouseY = e.clientY - imageRect.top;
    
    // Calculate lens position (centered on cursor, bounded by image)
    const lensX = Math.max(0, Math.min(mouseX - LENS_SIZE / 2, imageRect.width - LENS_SIZE));
    const lensY = Math.max(0, Math.min(mouseY - LENS_SIZE / 2, imageRect.height - LENS_SIZE));
    
    // Calculate the center of the lens relative to the image
    const lensCenterX = lensX + LENS_SIZE / 2;
    const lensCenterY = lensY + LENS_SIZE / 2;
    
    // Calculate zoom offset for background positioning
    const zoomX = (lensCenterX / imageRect.width) * 100;
    const zoomY = (lensCenterY / imageRect.height) * 100;
    
    setLensPosition({ x: lensX, y: lensY });
    setZoomOffset({ x: zoomX, y: zoomY });
  };

  const handleModalClose = () => {
    setSelectedImage(null);
    setIsZooming(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="relative py-16 bg-gradient-to-r from-gray-900 to-gray-800 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/newcollectionbanner.jpg.png')" }}
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.2 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            New Collection
          </motion.h1>
          <p className="text-[14px] md:text-base text-gray-300 max-w-2xl mx-auto">
            Discover our latest New Collection of premium shirts - crafted for comfort, style, and everyday elegance.
          </p>
        </div>
      </section>

      {/* Image Grid */}
      <section className="py-16 container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
          {imageList.map((src, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="group cursor-pointer"
              onClick={() => {
                setSelectedImage(src);
                setIsZooming(false);
              }}
            >
              <div className="relative overflow-hidden rounded-xl shadow-lg bg-white p-2">
                <img
                  src={src}
                  alt={`Collection ${index + 1}`}
                  className="w-full h-90 object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Modal - Different behavior for mobile vs desktop */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={handleModalClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={`relative ${isMobile ? 'w-full h-full' : 'flex gap-6 max-w-6xl w-full max-h-[90vh]'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleModalClose}
              className="absolute top-4 right-4 z-50 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
            >
              <X size={20} />
            </button>

            {/* Mobile View - Full Image Only */}
            {isMobile ? (
              <div className="flex items-center justify-center w-full h-full">
                <img
                  src={selectedImage}
                  alt="Selected item"
                  className="max-w-full max-h-full object-contain rounded-lg"
                  draggable={false}
                />
              </div>
            ) : (
              /* Desktop View - With Zoom Functionality */
              <>
                {/* Main Image Container with Lens */}
                <div className="relative flex-1 flex items-center justify-center bg-white/5 rounded-xl overflow-hidden">
                  <div
                    className="relative cursor-crosshair"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => setIsZooming(true)}
                    onMouseLeave={() => setIsZooming(false)}
                  >
                    <img
                      ref={imageRef}
                      src={selectedImage}
                      alt="Selected item"
                      className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-xl"
                      draggable={false}
                    />

                    {/* Light Magnifying Lens Overlay */}
                    {isZooming && (
                      <div
                        className="absolute pointer-events-none border-2 border-white/60 bg-white/10"
                        style={{
                          width: `${LENS_SIZE}px`,
                          height: `${LENS_SIZE}px`,
                          left: `${lensPosition.x}px`,
                          top: `${lensPosition.y}px`,
                          borderRadius: '8px',
                          boxShadow: '0 0 0 1px rgba(255,255,255,0.2)',
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Light Zoom Preview Panel */}
                {isZooming && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="w-80 h-80 flex-shrink-0 relative"
                  >
                    <div className="w-full h-full rounded-xl overflow-hidden shadow-xl border-2 border-white/30 bg-white">
                      <div
                        className="w-full h-full bg-no-repeat"
                        style={{
                          backgroundImage: `url(${selectedImage})`,
                          backgroundSize: `${ZOOM_FACTOR * 100}%`,
                          backgroundPosition: `${zoomOffset.x}% ${zoomOffset.y}%`,
                        }}
                      />
                    </div>
                    <div className="absolute -top-2 left-4 bg-white/80 text-gray-700 px-2 py-1 rounded text-xs font-medium shadow">
                      {ZOOM_FACTOR}x Zoom
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>

          {/* Instructions - Only show on desktop */}
          {!isMobile && (
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white/60 text-sm text-center">
              <p>Hover over the image to zoom • Click outside to close</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default NewCollection;